import { useSyncExternalStore, useCallback } from 'react';

type Subscriber = () => void;

/**
 * Which Web Storage area backs a store. `local` is shared across every tab on
 * the origin; `session` is private to a single tab (survives reload, never
 * crosses tabs).
 */
export type StorageKind = 'local' | 'session';

interface StoreEntry<T> {
  snapshot: T;
  subscribe: (cb: Subscriber) => () => void;
  getSnapshot: () => T;
  read: () => T;
  write: (value: T) => void;
}

// Keyed by `${kind}:${key}` so a local and a session store can share a key
// without colliding.
const stores = new Map<string, StoreEntry<unknown>>();

/** @internal Exposed for tests to reset cached store entries between runs. */
export function __resetStores() {
  stores.clear();
}

function getArea(kind: StorageKind): Storage {
  return kind === 'session' ? sessionStorage : localStorage;
}

function getOrCreateStore<T>(kind: StorageKind, key: string, defaultValue: T): StoreEntry<T> {
  const cacheKey = `${kind}:${key}`;
  const existing = stores.get(cacheKey);
  if (existing) return existing as StoreEntry<T>;

  const area = getArea(kind);
  const listeners = new Set<Subscriber>();

  function read(): T {
    try {
      const raw = area.getItem(key);
      if (raw === null) return defaultValue;
      return JSON.parse(raw) as T;
    } catch {
      return defaultValue;
    }
  }

  function notify() {
    for (const cb of listeners) cb();
  }

  const entry: StoreEntry<T> = {
    snapshot: read(),

    subscribe(cb: Subscriber) {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    },

    getSnapshot() {
      return entry.snapshot;
    },

    read,

    write(value: T) {
      entry.snapshot = value;
      area.setItem(key, JSON.stringify(value));
      // Same-tab subscribers all share this one entry, so notify them
      // directly. Other tabs pick up the change through the browser's native
      // `storage` event (localStorage only), handled by the listener below.
      notify();
    },
  };

  // Cross-tab sync: the browser fires a native `storage` event in *other* tabs
  // when this origin's `localStorage` changes. One window listener per
  // (kind, key) entry — *not* per subscribe: a mounted hook subscribes at
  // least once, so a per-subscribe listener meant N components on a key
  // installed N listeners, and every event fanned out N read()+notify()s
  // (O(N^2) across the app). The entry lives for the page's lifetime, so the
  // listener does too — no `removeEventListener` symmetry required.
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', (e: StorageEvent) => {
      // Native events fire only for localStorage; a session store must ignore
      // them (its data is per-tab), so discriminate by the event's area.
      if (e.storageArea && e.storageArea !== area) return;
      if (e.key === key || e.key === null) {
        entry.snapshot = read();
        notify();
      }
    });
  }

  stores.set(cacheKey, entry as StoreEntry<unknown>);
  return entry;
}

/**
 * Web Storage-backed state hook with reactive cross-component sync, where the
 * storage area is chosen at the call site. Use this when the area is dynamic
 * (e.g. a generic hook parameterized by config); otherwise prefer the
 * {@link useLocalStorageState} / {@link useSessionStorageState} wrappers.
 */
export function useWebStorageState<T>(
  kind: StorageKind,
  key: string,
  defaultValue: T,
): [T, (value: T | ((prev: T) => T)) => void] {
  const store = getOrCreateStore<T>(kind, key, defaultValue);

  const value = useSyncExternalStore(store.subscribe, store.getSnapshot, () => defaultValue);

  const setValue = useCallback(
    (next: T | ((prev: T) => T)) => {
      const resolved = typeof next === 'function' ? (next as (prev: T) => T)(store.read()) : next;
      store.write(resolved);
    },
    [store],
  );

  return [value, setValue];
}

/**
 * Generic localStorage-backed state hook with reactive cross-component sync.
 *
 * Multiple components (even on different pages) sharing the same `key` stay in
 * sync within a tab through a shared `useSyncExternalStore` store. Because
 * `localStorage` is shared by every tab on the origin, the browser's native
 * `storage` event also propagates writes across tabs. Reach for
 * {@link useSessionStorageState} when a value should stay private to one tab.
 */
export function useLocalStorageState<T>(
  key: string,
  defaultValue: T,
): [T, (value: T | ((prev: T) => T)) => void] {
  return useWebStorageState('local', key, defaultValue);
}

/**
 * sessionStorage-backed variant of {@link useLocalStorageState}.
 *
 * Same reactive cross-component sync within a tab, but the value lives in
 * `sessionStorage`: it survives a reload yet never crosses to other tabs.
 * Use this for transient per-tab UI state (e.g. a search box) that should
 * not mirror into a second tab on the same origin.
 */
export function useSessionStorageState<T>(
  key: string,
  defaultValue: T,
): [T, (value: T | ((prev: T) => T)) => void] {
  return useWebStorageState('session', key, defaultValue);
}
